import { OUTCOME_LABELS } from "@components/scheduling/campaignLabels";
import Button from "@components/ui/Button";
import Table, {
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableHeaderCell,
} from "@components/ui/Table";

export const PRIORITY_CONFIG = {
  high: { label: "High", classes: "bg-red-100 text-red-800", dot: "bg-red-500" },
  medium: { label: "Medium", classes: "bg-amber-100 text-amber-800", dot: "bg-amber-500" },
  low: { label: "Low", classes: "bg-gray-100 text-gray-700", dot: "bg-gray-400" },
};

export const REASON_LABELS = {
  cancelled: "Cancelled – no rebook",
  no_show: "No-show – no rebook",
  inactive: "Dormant 5–18 months",
  long_dormant: "Not seen in 18+ months",
  inactive_pms: "Inactive in PMS",
};

/** "21 months ago", "Under a month ago", "None on record", or an em dash when the
 * backend predates the field. */
export function lastVisitText(row) {
  const months = row.months_since_last_visit;
  if (months === undefined) return "—";
  if (months === null) return "None on record";
  if (months === 0) return "Under a month ago";
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

/**
 * One of the four reactivation lists. A selectable section offers a checkbox
 * per row and one in the header that selects the section's eligible rows (a
 * phone, not in a campaign); a view-only section shows the row checkbox
 * disabled with the reason as its title, so the row explains itself.
 */
export default function ReactivationListSection({
  title,
  rows,
  selectable,
  selectedIds,
  onToggleOne,
  onToggleRows,
  onMarkDoNotCall,
  busyPatientId,
  emptyText,
}) {
  const eligibleRows = selectable
    ? rows.filter((p) => p.phone && !p.in_campaign)
    : [];
  const selectedCount = eligibleRows.filter((p) =>
    selectedIds.has(p.external_patient_id)
  ).length;
  const allSelected =
    eligibleRows.length > 0 && selectedCount === eligibleRows.length;
  const someSelected = selectedCount > 0 && !allSelected;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-gray-800">
        {title} ({rows.length})
      </h2>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">{emptyText}</p>
      ) : (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell className="w-10">
                {selectable ? (
                  <input
                    type="checkbox"
                    aria-label={`Select all ${title.toLowerCase()}`}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    checked={allSelected}
                    // `indeterminate` is a DOM property with no attribute, so
                    // a partial selection reads as plain unchecked without it.
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    disabled={eligibleRows.length === 0}
                    onChange={() => onToggleRows(eligibleRows, !allSelected)}
                  />
                ) : (
                  <span className="sr-only">Select</span>
                )}
              </TableHeaderCell>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Phone</TableHeaderCell>
              <TableHeaderCell>Date of Birth</TableHeaderCell>
              <TableHeaderCell>Last visit</TableHeaderCell>
              <TableHeaderCell>Reason</TableHeaderCell>
              <TableHeaderCell>Priority</TableHeaderCell>
              <TableHeaderCell>Last contact</TableHeaderCell>
              <TableHeaderCell>Action</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((patient, idx) => {
              const priorityCfg = PRIORITY_CONFIG[patient.priority] || PRIORITY_CONFIG.medium;
              const reason = REASON_LABELS[patient.reason] || patient.reason;
              // The PMS does not always give us a name; the row shows an em
              // dash for it, so the controls must not read "Select null".
              const who = patient.name || "unnamed patient";
              let title;
              if (!selectable) title = `${reason}: view only`;
              else if (!patient.phone) title = "No phone number on file";
              return (
                <TableRow key={patient.external_patient_id || idx}>
                  <TableCell className="w-10">
                    <input
                      type="checkbox"
                      aria-label={`Select ${who}`}
                      title={title}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      checked={selectable && selectedIds.has(patient.external_patient_id)}
                      disabled={!selectable || !patient.phone || patient.in_campaign}
                      onChange={() => onToggleOne(patient)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>
                  <TableCell className="font-medium text-gray-900 whitespace-nowrap">
                    {patient.name || "—"}
                  </TableCell>
                  <TableCell className="text-gray-700 whitespace-nowrap">
                    {patient.phone ? (
                      <a href={`tel:${patient.phone}`} className="text-blue-600 hover:underline">
                        {patient.phone}
                      </a>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell className="text-gray-700 whitespace-nowrap">
                    {patient.date_of_birth
                      ? new Date(patient.date_of_birth + "T00:00:00").toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "—"}
                  </TableCell>
                  <TableCell className="text-gray-700 whitespace-nowrap">
                    {lastVisitText(patient)}
                  </TableCell>
                  <TableCell className="text-gray-700 whitespace-nowrap">{reason}</TableCell>
                  <TableCell>
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-3 text-xs font-medium h-6 ${priorityCfg.classes}`}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full ${priorityCfg.dot}`} />
                      {priorityCfg.label}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {patient.last_contacted_at ? (
                      <>
                        {new Date(patient.last_contacted_at).toLocaleDateString()}
                        {patient.last_outcome && (
                          <span className="block text-xs text-gray-500">
                            {OUTCOME_LABELS[patient.last_outcome] || patient.last_outcome}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-400">Not contacted</span>
                    )}
                    {patient.in_campaign && (
                      <span className="block text-xs text-blue-700">In a campaign</span>
                    )}
                  </TableCell>
                  <TableCell className="flex items-center gap-2">
                    <Button
                      color="light"
                      size="xs"
                      aria-label={`Do not call ${who}`}
                      disabled={busyPatientId === patient.external_patient_id}
                      onClick={() => onMarkDoNotCall(patient)}
                    >
                      {patient.do_not_call ? "Allow calls" : "Do not call"}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
